import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Box } from '@mui/material';

import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import AppLayout from './components/layout/AppLayout';

import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import EventCreatePage from './pages/EventCreatePage';
import EventDetailPage from './pages/EventDetailPage';
import EventRsvpPage from './pages/EventRsvpPage';
import CheckInPage from './pages/CheckInPage';
import PublicEventPage from './pages/PublicEventPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminHostsPage from './pages/admin/AdminHostsPage';
import AdminHostDetailPage from './pages/admin/AdminHostDetailPage';
import GuestGroupsPage from './pages/GuestGroupsPage';
import RsvpQuestionsManager from './pages/admin/RsvpQuestionsManager';
import ProfilePage from './pages/account/ProfilePage';
import NotificationPrefsPage from './pages/account/NotificationPrefsPage';
import EmailUsagePage from './pages/account/EmailUsagePage';
import BillingHistoryPage from './pages/account/BillingHistoryPage';
import PromoCodesPage from './pages/admin/PromoCodesPage';
import PrivacyAdminPage from './pages/admin/PrivacyAdminPage';
import PrivacyRequestPage from './pages/PrivacyRequestPage';
import DataRetentionPage from './pages/account/DataRetentionPage';
import EventAnalyticsPage from './pages/EventAnalyticsPage';
import PlatformAnalyticsPage from './pages/admin/PlatformAnalyticsPage';
import BrandingPage from './pages/account/BrandingPage';
import ApiKeysPage from './pages/account/ApiKeysPage';
import ImageLibraryPage from './pages/account/ImageLibraryPage';
import FeatureFlagsPage from './pages/admin/FeatureFlagsPage';
import TemplateManagerPage from './pages/admin/TemplateManagerPage';
import AbuseReportsPage from './pages/admin/AbuseReportsPage';
import EmailHealthPage from './pages/admin/EmailHealthPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

export default function App() {
  return (
    <AuthProvider>
      <Box sx={{ minHeight: '100vh' }}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/rsvp/:token" element={<EventRsvpPage />} />
          <Route path="/e/:slug" element={<PublicEventPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/privacy" element={<PrivacyRequestPage />} />

          {/* Protected routes */}
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/events/new" element={<EventCreatePage />} />
            <Route path="/events/:eventId" element={<EventDetailPage />} />
            <Route path="/guest-groups" element={<GuestGroupsPage />} />
            <Route path="/events/:eventId/checkin" element={<CheckInPage />} />
            <Route path="/events/:eventId/rsvp-questions" element={<RsvpQuestionsManager />} />

            {/* Account routes */}
            <Route path="/account/profile" element={<ProfilePage />} />
            <Route path="/account/notifications" element={<NotificationPrefsPage />} />
            <Route path="/account/email-usage" element={<EmailUsagePage />} />
            <Route path="/account/billing" element={<BillingHistoryPage />} />
            <Route path="/account/data-retention" element={<DataRetentionPage />} />
            <Route path="/events/:eventId/analytics" element={<EventAnalyticsPage />} />

            {/* Account / Pro routes */}
            <Route path="/account/branding" element={<BrandingPage />} />
            <Route path="/account/api-keys" element={<ApiKeysPage />} />
            <Route path="/account/images" element={<ImageLibraryPage />} />

            {/* Admin routes */}
            <Route path="/admin" element={<AdminRoute><Outlet /></AdminRoute>}>
              <Route index element={<AdminDashboardPage />} />
              <Route path="hosts" element={<AdminHostsPage />} />
              <Route path="hosts/:hostId" element={<AdminHostDetailPage />} />
              <Route path="promo-codes" element={<PromoCodesPage />} />
              <Route path="privacy" element={<PrivacyAdminPage />} />
              <Route path="analytics" element={<PlatformAnalyticsPage />} />
              <Route path="feature-flags" element={<FeatureFlagsPage />} />
              <Route path="templates" element={<TemplateManagerPage />} />
              <Route path="abuse-reports" element={<AbuseReportsPage />} />
              <Route path="email-health" element={<EmailHealthPage />} />
            </Route>
          </Route>

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Box>
    </AuthProvider>
  );
}
